/**
 * Painel de absenteísmo.
 *
 * Responde o que a empresa cliente pergunta e o sistema nunca respondeu: quanto
 * do tempo de trabalho está sendo perdido, por qual doença, em qual setor.
 *
 * Decisões de leitura, e o porquê de cada uma:
 *
 * - **Um eixo por gráfico.** A série mensal tem duas medidas (quantidade de
 *   afastamentos e dias perdidos) em escalas diferentes. Eixo duplo faria
 *   parecer que uma cruza a outra, o que é artefato da escala, não do dado. A
 *   linha mostra DIAS — a métrica que a empresa sente — e a contagem vai no
 *   tooltip.
 * - **Série única por gráfico, então um só tom.** Sem paleta categórica: cor
 *   aqui não distingue entidade, só marca o dado contra o fundo. Sem legenda,
 *   porque o título já nomeia a série.
 * - **Ordenado por DIAS, não por contagem.** Dez resfriados de um dia pesam
 *   menos na operação do que uma hérnia de trinta.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, Stack, Typography, useTheme } from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from 'recharts';
import { PageShell } from '../../components';
import absenteeismService from '../../services/absenteeismService';
import type { PainelDeAbsenteismo, ItemAgrupado } from '../../types/absenteeism';

/**
 * Tooltip dos dois gráficos: mostra a medida plotada (dias) junto da que ficou
 * de fora do eixo (quantidade de afastamentos). É assim que as duas medidas
 * convivem sem um segundo eixo.
 */
function formatarPerdidos(valor: unknown, _nome: unknown, item: unknown): [string, string] {
  const payload = (item as { payload?: { afastamentos?: number } } | undefined)?.payload;
  const afastamentos = payload?.afastamentos ?? 0;
  return [`${Number(valor ?? 0)} dias em ${afastamentos} afastamento(s)`, 'Perdidos'];
}

/** Quanto de rótulo cabe na canaleta do eixo antes de atropelar o gráfico. */
const LIMITE_DO_ROTULO = 34;

/**
 * Encurta sem cortar palavra ao meio: "Efeitos do ruído sobre o ouvid" lê como
 * defeito, "Efeitos do ruído sobre o…" lê como escolha. O nome inteiro continua
 * disponível no tooltip, então nada se perde.
 */
function encurtar(texto: string): string {
  const limpo = texto.trim();
  if (limpo.length <= LIMITE_DO_ROTULO) return limpo;
  const corte = limpo.slice(0, LIMITE_DO_ROTULO);
  const ultimoEspaco = corte.lastIndexOf(' ');
  return `${(ultimoEspaco > LIMITE_DO_ROTULO / 2 ? corte.slice(0, ultimoEspaco) : corte).trimEnd()}…`;
}

/** 2026-03 -> mar/26 */
function rotuloDoMes(iso: string): string {
  const [ano, mes] = iso.split('-');
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${nomes[Number(mes) - 1]}/${ano.slice(2)}`;
}

interface TileProps {
  rotulo: string;
  valor: string;
  nota?: string;
}

const Tile: React.FC<TileProps> = ({ rotulo, valor, nota }) => (
  <Card variant="outlined" sx={{ flex: '1 1 160px', minWidth: 160 }}>
    <CardContent sx={{ py: 2 }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.06em' }}
      >
        {rotulo}
      </Typography>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, mt: 0.5, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
      >
        {valor}
      </Typography>
      {nota && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {nota}
        </Typography>
      )}
    </CardContent>
  </Card>
);

interface BlocoBarrasProps {
  titulo: string;
  subtitulo: string;
  dados: ItemAgrupado[];
  cor: string;
  grade: string;
  eixo: string;
  mostrarChave?: boolean;
}

const BlocoBarras: React.FC<BlocoBarrasProps> = ({
  titulo,
  subtitulo,
  dados,
  cor,
  grade,
  eixo,
  mostrarChave,
}) => {
  const linhas = dados.map((d) => {
    const completo = mostrarChave ? `${d.chave} · ${d.descricao ?? ''}`.trim() : d.chave;
    return {
      nome: encurtar(completo),
      completo,
      dias: d.dias,
      afastamentos: d.afastamentos,
    };
  });

  return (
    <Card variant="outlined" sx={{ flex: '1 1 460px', minWidth: 320 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {titulo}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {subtitulo}
        </Typography>
        <Box sx={{ height: Math.max(200, linhas.length * 34), mt: 2 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={linhas} layout="vertical" margin={{ left: 4, right: 44, top: 4 }}>
              <CartesianGrid horizontal={false} stroke={grade} />
              <XAxis type="number" tick={{ fill: eixo, fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis
                type="category"
                dataKey="nome"
                width={170}
                tick={{ fill: eixo, fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: grade }}
                formatter={formatarPerdidos}
                // O eixo mostra o rótulo encurtado; aqui vem o nome por extenso.
                labelFormatter={(_r, carga) =>
                  (carga?.[0]?.payload as { completo?: string } | undefined)?.completo ?? ''
                }
              />
              <Bar dataKey="dias" fill={cor} radius={[0, 4, 4, 0]} barSize={16}>
                <LabelList dataKey="dias" position="right" style={{ fill: eixo, fontSize: 12 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export const AbsenteeismPage: React.FC = () => {
  const tema = useTheme();
  const [painel, setPainel] = useState<PainelDeAbsenteismo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // Um só tom para os dados: cada gráfico é de série única, então a cor não
  // distingue entidade nenhuma — só separa o dado do fundo.
  const cor = tema.palette.primary.main;
  const grade = tema.palette.divider;
  const eixo = tema.palette.text.secondary;

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setPainel(await absenteeismService.painel());
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar o painel');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const serie = useMemo(
    () => (painel?.serieMensal ?? []).map((p) => ({ ...p, rotulo: rotuloDoMes(p.mes) })),
    [painel]
  );

  const vazio = !carregando && painel !== null && painel.resumo.totalAfastamentos === 0;

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Absenteísmo' }]}
      title="Absenteísmo"
      subtitle={
        painel
          ? `De ${painel.periodo.inicio.split('-').reverse().join('/')} a ${painel.periodo.fim
              .split('-')
              .reverse()
              .join('/')}`
          : 'Últimos 12 meses'
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {vazio && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Nenhum afastamento registrado nesta empresa no período. O painel se preenche sozinho
          conforme as licenças médicas forem lançadas em Licença Médica.
        </Alert>
      )}

      {painel && !vazio && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Tile
              rotulo="Taxa de absenteísmo"
              valor={painel.resumo.taxaAbsenteismo !== null ? `${painel.resumo.taxaAbsenteismo}%` : '—'}
              nota={`base de ${painel.baseDeFuncionarios} funcionários`}
            />
            <Tile rotulo="Dias perdidos" valor={String(painel.resumo.diasPerdidos)} />
            <Tile rotulo="Afastamentos" valor={String(painel.resumo.totalAfastamentos)} />
            <Tile
              rotulo="Duração média"
              valor={`${painel.resumo.duracaoMediaDias}`}
              nota="dias por afastamento"
            />
            <Tile
              rotulo="Trabalhadores"
              valor={String(painel.resumo.trabalhadoresAfastados)}
              nota="com ao menos um afastamento"
            />
          </Stack>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Dias perdidos por mês
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                A quantidade de afastamentos aparece ao passar o mouse
              </Typography>
              <Box sx={{ height: 260, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={serie} margin={{ left: 4, right: 12, top: 8 }}>
                    <CartesianGrid vertical={false} stroke={grade} />
                    <XAxis dataKey="rotulo" tick={{ fill: eixo, fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: eixo, fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      formatter={formatarPerdidos}
                    />
                    <Line
                      type="monotone"
                      dataKey="dias"
                      stroke={cor}
                      strokeWidth={2}
                      dot={{ r: 4, fill: cor, strokeWidth: 0 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <BlocoBarras
              titulo="Principais causas (CID-10)"
              subtitulo="Ordenado por dias perdidos, não por número de casos"
              dados={painel.porCid}
              cor={cor}
              grade={grade}
              eixo={eixo}
              mostrarChave
            />
            <BlocoBarras
              titulo="Por setor"
              subtitulo="Onde o tempo está sendo perdido"
              dados={painel.porSetor}
              cor={cor}
              grade={grade}
              eixo={eixo}
            />
          </Stack>

          <BlocoBarras
            titulo="Por empresa"
            subtitulo="Onde o tempo está sendo perdido, por empresa"
            dados={painel.porEmpresa}
            cor={cor}
            grade={grade}
            eixo={eixo}
          />
        </Stack>
      )}
    </PageShell>
  );
};

export default AbsenteeismPage;
